import { MatDialog } from '@angular/material/dialog';
import { CallbackOutcomeDialogComponent, CALLBACK_OUTCOMES } from './callback-outcome-dialog.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { RingostatCallService } from 'src/app/ringostat/ringostat-call.service';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import {
  B2cCallbackRequest,
  B2cCallbackRequestListResponse,
  B2cCallbackRequestStatusFilter
} from 'src/typings';
import { B2cCallbackService } from './b2c-callback.service';

@Component({
  selector: 'app-b2c-callback-journal',
  templateUrl: './callback-journal.component.html',
  styleUrls: ['./callback-journal.component.scss']
})
export class B2cCallbackJournalComponent implements OnInit, OnDestroy {
  readonly displayedColumns = ['request', 'client', 'page', 'status', 'closed', 'actions'];

  readonly statusOptions: Array<{ value: B2cCallbackRequestStatusFilter; label: string }> = [
    { value: 'ALL', label: 'Все' },
    { value: 'NEW', label: 'Новые' },
    { value: 'DONE', label: 'Закрытые' }
  ];

  response: B2cCallbackRequestListResponse | null = null;

  callbacks: B2cCallbackRequest[] = [];

  loading = false;

  error: string | null = null;

  statusFilter: B2cCallbackRequestStatusFilter = 'NEW';

  completingRequestId: number | null = null;

  callingRequestId: number | null = null;

  private createdSubscription?: Subscription;

  private updatedSubscription?: Subscription;

  constructor(
    private readonly b2cCallbackService: B2cCallbackService,
    private readonly ringostatCallService: RingostatCallService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.load();
    this.createdSubscription = this.b2cCallbackService.createdGW.subscribe(() => this.load(false));
    this.updatedSubscription = this.b2cCallbackService.updatedGW.subscribe(() => this.load(false));
  }

  ngOnDestroy(): void {
    this.createdSubscription?.unsubscribe();
    this.updatedSubscription?.unsubscribe();
  }

  load(showLoading = true) {
    if (showLoading) {
      this.loading = true;
    }
    this.error = null;

    this.b2cCallbackService
      .getJournal({
        status: this.statusFilter,
        limit: 100
      })
      .pipe(
        finalize(() => {
          if (showLoading) {
            this.loading = false;
          }
        })
      )
      .subscribe({
        next: (response) => {
          this.response = response;
          this.callbacks = response.data || [];
        },
        error: () => {
          this.response = null;
          this.callbacks = [];
          this.error = 'Не удалось загрузить журнал перезвонов.';
        }
      });
  }

  setStatusFilter(value: B2cCallbackRequestStatusFilter) {
    this.statusFilter = value;
    this.load();
  }

  trackByRequest(_: number, request: B2cCallbackRequest) {
    return request.id;
  }

  get totalLabel() {
    const total = this.response?.total || 0;
    if (this.statusFilter === 'NEW') return `${total} ожидают звонка`;
    if (this.statusFilter === 'DONE') return `${total} закрыто`;
    return `${total} заявок`;
  }

  get newInList() {
    return this.callbacks.filter((request) => request.status === 'NEW').length;
  }

  get doneInList() {
    return this.callbacks.filter((request) => request.status === 'DONE').length;
  }

  getRequestDateLabel(request: B2cCallbackRequest) {
    return this.formatDateTime(request.date_created);
  }

  getClosedLabel(request: B2cCallbackRequest) {
    if (request.status !== 'DONE') return 'Ожидает звонка';
    return this.formatDateTime(request.date_closed) || 'Закрыто';
  }

  getClosedMeta(request: B2cCallbackRequest) {
    if (request.status !== 'DONE') return '';
    return request.completed_by_user_name || 'Менеджер не указан';
  }

  getClientLink(request: B2cCallbackRequest) {
    return request.client_id ? ['/client/profile', request.client_id] : null;
  }

  getClientLabel(request: B2cCallbackRequest) {
    return request.client_name || request.name || 'Клиент без имени';
  }

  getClientMeta(request: B2cCallbackRequest) {
    const phone = this.getPhoneLabel(request);
    if (phone !== '-') return phone;
    return request.client_id ? `Клиент #${request.client_id}` : 'Без карточки клиента';
  }

  getPhoneLabel(request: B2cCallbackRequest) {
    const digits = this.getPhoneDigits(request);
    if (!digits) return '-';
    if (digits.length === 12 && digits.startsWith('38')) return `+${digits}`;
    if (digits.length === 9) return `+380${digits}`;
    if (digits.length === 10 && digits.startsWith('0')) return `+38${digits}`;
    return `+38${digits}`;
  }

  getClickToCallPhone(request: B2cCallbackRequest) {
    const digits = this.getPhoneDigits(request);
    if (!digits) return '';
    if (digits.length === 12 && digits.startsWith('38')) return `+${digits}`;
    if (digits.length === 9) return `+380${digits}`;
    if (digits.length === 10 && digits.startsWith('0')) return `+38${digits}`;
    return `+38${digits}`;
  }

  canClickToCall(request: B2cCallbackRequest) {
    return Boolean(this.getClickToCallPhone(request));
  }

  getStatusLabel(request: B2cCallbackRequest) {
    return request.status === 'DONE' ? 'Закрыто' : 'Нужно перезвонить';
  }

  getStatusClass(request: B2cCallbackRequest) {
    return request.status === 'DONE'
      ? 'statusChip statusChip--done'
      : 'statusChip statusChip--new';
  }

  getOutcomeLabel(request: B2cCallbackRequest): string {
    return CALLBACK_OUTCOMES.find((item) => item.value === request.outcome)?.label ||
      (request.status === 'DONE' ? 'Результат не указан' : '');
  }

  getPageUrl(request: B2cCallbackRequest) {
    return String(request.page_url || '').trim();
  }

  getPageLabel(request: B2cCallbackRequest) {
    const pageUrl = this.getPageUrl(request);
    if (!pageUrl) return '-';

    try {
      const parsed = new URL(pageUrl);
      return parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : parsed.hostname;
    } catch {
      return pageUrl;
    }
  }

  async complete(request: B2cCallbackRequest) {
    if (request.status !== 'NEW' || this.completingRequestId) return;

    this.completingRequestId = request.id;
    try {
      const result = await firstValueFrom(this.dialog.open(CallbackOutcomeDialogComponent, {
        width: '440px', maxWidth: '95vw'
      }).afterClosed());
      if (!result) return;
      await firstValueFrom(this.b2cCallbackService.complete(request.id, result));
      this.notificationService.notice(result.outcome === 'NO_ANSWER' ? 'Нет ответа — заявка остаётся в очереди' : `Результат заявки №${request.id} сохранён`);
      this.load(false);
    } catch (error: unknown) {
      this.notificationService.warning(
        extractApiErrorMessage(error, 'Не удалось закрыть заявку на перезвон')
      );
    } finally {
      this.completingRequestId = null;
    }
  }

  async clickToCall(request: B2cCallbackRequest) {
    const phone = this.getClickToCallPhone(request);
    if (!phone || this.callingRequestId) return;

    this.callingRequestId = request.id;
    try {
      await firstValueFrom(
        this.ringostatCallService.clickToCall({
          phone,
          clientId: request.client_id || undefined
        })
      );
      this.notificationService.notice('Звонок клиенту запущен: после ответа клиента примите вызов');
    } catch (error: unknown) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось запустить звонок')
      );
    } finally {
      this.callingRequestId = null;
    }
  }

  private getPhoneDigits(request: B2cCallbackRequest) {
    return String(request.phone || request.client_phone || '').replace(/\D/g, '');
  }

  private formatDateTime(value?: Date | string | null) {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }
}
